import React, { useEffect, useState, Dispatch, SetStateAction } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Grid,
    makeStyles,
    IconButton,
    Typography,
    CircularProgress,
    Tooltip
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ErrorIcon from "@material-ui/icons/Error";
import {cloneDeep, remove} from "lodash"
import {updateC3ExpCollection} from "./C3Services/contentControl"

const LoggedInUserState = require("shared/ui/user/LoggedInUserState");

const useStyles = makeStyles({
    breakpointSection: {
        position: "relative",
        display: "flex",
        flexFlow: "row nowrap",
        margin: "0",
        paddingBottom: "25px",
        color: "#343E43",
    },
    breakpointContainer: {
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexGrow: 1,
        height: "170px",
        padding: "0px 12px",
        flexFlow: "column nowrap",
        borderLeft: "1px dashed #343E43",
        minWidth: "1px",
        "&:first-child": {
            border: "none",

            "& div#threshold-value": {
                display: "none",
            },
        },
    },
    intervalIndicator: {
        backgroundPosition: "center top",
        height: "35px",
        width: "44px",
        '&[id*="S"]': {
            backgroundImage: "url(../../../../images/settings/icon_bpvariant_mobile.png)",
        },
        '&[id*="Medium"]': {
            backgroundImage: "url(../../../../images/settings/icon_bpvariant_tablet.png)",
        },
        '&[id*="L"]': {
            backgroundImage: "url(../../../../images/settings/icon_bpvariant_desktop.png)",
        },
    },
    breakpointValue: {
        position: "absolute",
        bottom: "-30px",
        left: "-12px",
        width: "70px",
        color: "#343E43",
    },
    assignBreakpointIcon: {
        position: "relative",
        cursor: "pointer",
        backgroundImage: "url(../../../../images/breakpoint_group_ko.png)",
        height: "17px",
        width: "28px",
        margin: "0px 10px",
        opacity: "0.7",
        transition: "0.25s",
        "&:hover": {
            opacity: "1",
        },
    },
    iconWrapper: {
        border: "1px solid #fff",
        borderTop: "none",
        borderBottom: "none",
        margin: "0px 15px",
        padding: '4px',
    },
    breakpointLabel: {
        display: "inlineBlock",
        marginTop: "10px",
    },
    selectBreakpoint: {
        marginTop: "15px",
        width: "95%",
        fontSize: "14px",
    },
    exclamationIcon: {
        position: "absolute",
        top: "-8px",
        right: "-4px",
        color: "#626262",
        backgroundColor: "#fff",
    },
    circularProgress: {
        '&.MuiCircularProgress-colorPrimary':{
            color: "#fff"
        }
    },
    dialog: {
        width: "700px",
        maxWidth: "700px",
        height: "402px",
    },
    dialogHeader: {
        color: "#343E43",
        backgroundColor: "#FFFFFF",
    },
    dialogTitle: {
        padding: "20px",
        flex: 1,
        "& .MuiTypography-root": {
            fontSize: "26px",
            lineHeight: "18px",
            fontWeight: "300",
        },
    },

    subHeading: {
        fontSize: "14px",
        lineHeight: "18px",
        fontWeight: 500,
        fontFamily: "Helvetica Neue,Helvetica,Arial,sans-serif",
        marginBottom: "10px",
        color: "#343e43",
    },

    tooltipPopper: {
        width: "200px",
        borderRadius: '1px',
        backgroundColor: '#fff',
        top: "-10px !important" ,
    },

    tooltipLabel : { 
        backgroundColor: '#fff',
        height: "80px",
        fontSize: "14px",
        fontWeight: 300,
        color: 'black',
        border: '1px solid #343e43',
        margin: "0px",
        "& .MuiTooltip-arrow":{
            color: "#fff",
            "&:before": {
                border: "1px solid #343e43"
            },
        }
    },

    btn: {
        textTransform: "none",
        margin: "0px 12px 12px",
        fontSize: "15px",
        padding: "0 18px",
        height: "32px",
        lineHeight: "32px",
        borderRadius: "0",
        transition: "0.25s",

        "&.MuiButton-contained": {
            color: "#fff",
            backgroundColor: "#8C01FF",
            "&:hover": {
                backgroundColor: "#cd00ff",
                borderColor: "#204d74",
            },
        },

        "&.MuiButton-outlined": {
            border: "1px solid",
            borderColor: "#757575",
            color: "#757575",
            backgroundColor: "#fff",
            "&:hover": {
                backgroundColor: "#fff",
                color: "#8C01FF",
                borderColor: "#8C01FF",
            },
        },
    },
});

interface ExperienceDetails {
    experienceId: string;
    threshold: number;
    language: string;
}

interface AssignBreakpointProps {
    collectionDetails : any;
    uniqueExperiences: string[];
    currentLanguageExperiences: ExperienceDetails[];
    currentLanguageExpJSON: any[]; // It will contain the ExperienceJSON all experiences of currentLanguage
    setCollectionDetails: Dispatch<SetStateAction<any>>;
    getChildExpIdForLang: (expId: string, language: string) => string;
}

export default function AssignBreakpoint(props: AssignBreakpointProps) {
    const { currentLanguageExperiences: expCollections, 
        currentLanguageExpJSON, 
        uniqueExperiences,
        collectionDetails,
        setCollectionDetails,
        getChildExpIdForLang
     } = props;
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const {defaultLanguage} = collectionDetails; // represent base language.
    const {language: currentLanguage} = expCollections[0] // represent language of current exp opened in editor.
    const [updatedExpCollections, setUpdatedExpCollections] = useState(expCollections);
    const [existingThresholds, setExistingThresholds] = useState<number[]>([])
    const [breakpointsUpdated, setBreakpointsUpdated] = useState<boolean>(false);
    const [breakpointNames, setBreakpointNames] = useState<string[]>([]);
    const breakpointCollection = LoggedInUserState.get("companySettings").get("breakpoints");
    const lowerBounds = breakpointCollection.getLowerBounds();
    const thresholdValues = lowerBounds.map((breakpoint) => breakpoint.get("threshold")); // [0,768,992]
    const [experienceCount, setExperienceCount] = useState<any>({});
    const [updatingBreakpoints, setUpdatingBreakpoints] = useState<boolean>(false);
    const [tooltipText, setTooltipText] = useState("");
    const [updateBreakpointsClicked, setUpdateBreakpointsClicked] = useState<boolean>(false);
    const [allExpDetails, setAllExpDetails] = useState(collectionDetails.experiences);

    useEffect(() => {
        const experienceList = allExpDetails.map((exp) => exp.experienceId);
        const expCount = experienceList.reduce((obj, expId) => {
            if (obj[expId]) obj[expId] += 1;
            else obj[expId] = 1;
            return obj;
        }, {});
        setExperienceCount(expCount);
    }, [expCollections, allExpDetails]);

    useEffect(() => {
        const thresholdsList = setupInitialBreakpointView();
        setBreakpointsUpdated(isBreakpintsUpdated(thresholdsList, thresholdValues));
        generateToolTipText();
    }, [expCollections]);

    const generateToolTipText = () => {
        let str = "The breakpoint settings have been changed to";
        const length = thresholdValues.length;
        thresholdValues.slice(1, length - 1).forEach((value: number) => str += ` ${value},`);
        str += ` and ${thresholdValues[length - 1]}.`;
        setTooltipText(str);
    }
    
    const handleCancel = () => {
        setOpen(false);
        setupInitialBreakpointView();
        setAllExpDetails(collectionDetails.experiences);
        if(updateBreakpointsClicked){
            setBreakpointsUpdated(true);
            setUpdateBreakpointsClicked(false);
        }
    };

    const setupInitialBreakpointView = () => {
        setUpdatedExpCollections(expCollections);
        const thresholdsList = expCollections.filter((exp) => exp.threshold !== null).map((exp) => exp.threshold);
        thresholdsList.sort((a,b) => a - b);
        setExistingThresholds(thresholdsList);
        setBreakpointNames(breakpointCollection.generateNaming(thresholdsList.length - 1));
        return thresholdsList;
    }
    
    /**
     * This method check if user has actually updated threshold for experiences or not.
     */
    const isExperiencesThresholdUpdated = (updatedExpCollections: ExperienceDetails[]) => { 
        if(updateBreakpointsClicked) return true;
        for(let i = 0; i < existingThresholds.length; i++){
            const {experienceId: expId1} = updatedExpCollections.find(({threshold}) => threshold === existingThresholds[i]) as ExperienceDetails;
            const {experienceId: expId2} = expCollections.find(({threshold}) => threshold === existingThresholds[i]) as ExperienceDetails;
            if(expId1 !== expId2){
                return true
            }
        }
        return false;
    };

    const getTotalLanguages = ()=>(
        Array.from(new Set(
                collectionDetails.experiences
                .map(expDetail => expDetail.language)
        )) as string[]
    )

    const handleDropdownChange = (e, thresholdValueToAssign) => {
         const currentExperienceId = e.target.value;
         const totalLanguages = getTotalLanguages();

        //Handling the experience to which onChange => thresholdValueToAssign was used.
        const prevExperience = updatedExpCollections.find((experience) => experience.threshold === thresholdValueToAssign) as ExperienceDetails;
        const {experienceId: prevExperienceId, threshold:prevThreshold } = prevExperience

        let newExpCollections = cloneDeep(allExpDetails);
        totalLanguages.forEach((language) => {
            if(language === defaultLanguage){
                 handleThresholdChange(newExpCollections,language, prevExperienceId, prevThreshold, currentExperienceId, thresholdValueToAssign);
            } else {
                const childOfPrevExp = getChildExpIdForLang(prevExperienceId, language);
                const childOfNewAssignedExp = getChildExpIdForLang(currentExperienceId, language);
                handleThresholdChange(newExpCollections, language, childOfPrevExp, prevThreshold, childOfNewAssignedExp, thresholdValueToAssign);
            }

        });
        setAllExpDetails(newExpCollections);
    }

    const handleThresholdChange = (newExpCollections, currentLang, prevExperienceId, prevThreshold, currentExperienceId, thresholdValueToAssign) => {
        if(experienceCount[prevExperienceId] > 1){
            remove(newExpCollections, ({threshold, language}) => (currentLang === language && threshold === prevThreshold));
            setExperienceCount(prev => ({
                ...prev,
                [prevExperienceId]: prev[prevExperienceId] - 1,
            }));
        } else {
            newExpCollections.forEach((expDetails: any )=> {
                if(expDetails.experienceId === prevExperienceId && expDetails.threshold === prevThreshold){
                    expDetails.threshold = null;
                    return;
                }
            });
        }

        //Handle the experience which was selected from dropdown.
        const unAssignedExperience = newExpCollections
            .find(({experienceId, threshold}) => (experienceId === currentExperienceId && threshold === null));

        if(unAssignedExperience !== undefined){
            unAssignedExperience.threshold = thresholdValueToAssign;
        } else {
            newExpCollections.push({
                experienceId: currentExperienceId,
                threshold: thresholdValueToAssign,
                language: currentLang, // as for all experience language will be same for current List
            })
             setExperienceCount(prev => ({
                ...prev,
                [currentExperienceId]: prev[currentExperienceId] + 1,
            }));
        }
        if(currentLang === currentLanguage){
            setUpdatedExpCollections(newExpCollections.filter(({language}) => language === currentLanguage));
        }
    };

    /**
     * This method check if user has updated his breakpoint settings
     * @param currentThreshold: Threshold with respect to current experience which is open in editor
     * @param thresholdFromSettings: Threshold value according to the settings
     * @return boolean. true have been updated from settings
     */
    const isBreakpintsUpdated = (currentThreshold: number[], thresholdFromSettings: number[]) => {
        let length1 = currentThreshold.length;
        let length2 = thresholdFromSettings.length;

        if (length1 !== length2) return true;

        currentThreshold.sort((a, b) => a - b);
        thresholdFromSettings.sort((a, b) => a - b);

        while (--length1 >= 0) {
            if (thresholdFromSettings[length1] !== currentThreshold[length1]) {
                return true;
            }
        }
        return false;
    };

    const updateExperienceCollections = async () => {
        let updatedExpCollectionDetails= cloneDeep(collectionDetails);
        let updatedAllExpDetails= cloneDeep(allExpDetails);
        //To make sure sort the experiences according to threshold and all experiences with threshold value of null will be last.
        const unAssignedExperiences = remove(updatedAllExpDetails, ({threshold}) => threshold === null);
        const baseLangExperiences = remove(updatedAllExpDetails, ({language}) => language === defaultLanguage) as any;

        updatedAllExpDetails.sort((a,b) => a.threshold - b.threshold);
        baseLangExperiences.sort((a,b) => a.threshold - b.threshold);
        const experiencesInOrder = [...baseLangExperiences, ...updatedAllExpDetails, ...unAssignedExperiences];
        if(!isExperiencesThresholdUpdated(experiencesInOrder)) {
            setOpen(false);
            return;
        } 
        updatedExpCollectionDetails.experiences = experiencesInOrder;
        setUpdatingBreakpoints(true);
        setAllExpDetails(experiencesInOrder);
        setCollectionDetails(updatedExpCollectionDetails);
        updateC3ExpCollection(updatedExpCollectionDetails)
            .then((response) =>{
                if(response) {
                    setUpdatingBreakpoints(false);
                    setOpen(false);
                    setUpdateBreakpointsClicked(false);
                }
            });
    }

    const updateNonBaseExpCollection = (nonBaseExpCollections: any, expDetail: ExperienceDetails, action: string) => {
        const totalLanguages = getTotalLanguages().filter(lang => lang !== defaultLanguage);
        const { experienceId } = expDetail;
        let targetExpDetail: any;
        totalLanguages.forEach((language) => {
            const childExp = getChildExpIdForLang(experienceId, language);
            targetExpDetail = nonBaseExpCollections
                .find(({experienceId, threshold}) => experienceId === childExp && threshold === expDetail.threshold);
            if(action === 'DELETE'){  // In case of multiple entry of same ExperienceId delete the entry.
                remove(
                    nonBaseExpCollections, 
                    ({experienceId, threshold}) => (experienceId === targetExpDetail.experienceId) && (threshold === targetExpDetail.threshold)
                );
            } else if(action === 'UPDATE') { // In case of only one entry of ExperienceId will assign nulll to threshold.
                targetExpDetail.threshold = null;
            } else if (action === 'ADD') {  // In case we have to add new threshold to ExperienceId and other entry of experienceId is already assigned
                nonBaseExpCollections.push({
                    experienceId: childExp,
                    threshold: expDetail.threshold,
                    language,
                });
            }
        })
    }

    // This function will update the breakpoint assign view when user click on 'Update Breakpoints'
    const handleOnClickUpdate = () => {
        setUpdateBreakpointsClicked(true);
   
        let newExpCollections = cloneDeep(updatedExpCollections) as any;
        const nonBaseExpCollections = cloneDeep(allExpDetails.filter(({language}) => language !== defaultLanguage)) as any;
        const allExperienceCount : any = newExpCollections
            .map(exp => exp.experienceId)
            .reduce((obj, expId) => {
                if (obj[expId]) obj[expId] += 1;
                else obj[expId] = 1;
                return obj;
            }, {});

        // If previously breakpoints are deleted, we need to handle the experiences which are assigned to that breakpoint.
        newExpCollections = newExpCollections.map((expDetail) => {
            const {threshold} = expDetail;
            if(!thresholdValues.includes(threshold)){
                if(allExperienceCount[expDetail.experienceId] === 1){
                    updateNonBaseExpCollection(nonBaseExpCollections, expDetail, "UPDATE");
                    expDetail.threshold = null;
                    return expDetail;
                } else {
                    allExperienceCount[expDetail.experienceId] -= 1;
                    updateNonBaseExpCollection(nonBaseExpCollections, expDetail, "DELETE")
                    return undefined;
                }
            }
            return expDetail;
        }).filter(expDetail => expDetail);

        const { experienceId: baseExpId, 
            language: currentLanguage
        } = updatedExpCollections.find(({threshold}) => threshold === 0) as ExperienceDetails;

        thresholdValues.forEach((value) =>{
            if(existingThresholds.indexOf(value) === -1){
                const newExpCollectionEntry = {
                    experienceId: baseExpId,
                    threshold: value,
                    language: currentLanguage
                };
                newExpCollections.push(newExpCollectionEntry);
                updateNonBaseExpCollection(nonBaseExpCollections, newExpCollectionEntry , "ADD")
            }
        });

        setUpdatedExpCollections(newExpCollections);
        setAllExpDetails([...newExpCollections, ... nonBaseExpCollections]);
        setBreakpointsUpdated(false);
        setExistingThresholds(thresholdValues);
        setBreakpointNames(breakpointCollection.generateNaming(thresholdValues.length - 1));
    }

    return (
        <>
            <Dialog
                classes={{ paper: classes.dialog }}
                open={open}
                onClose={handleCancel}
                className="breakpoints-assign-lightbox"
            >
                <Grid container direction="row" wrap="nowrap" className={classes.dialogHeader}>
                    <DialogTitle className={classes.dialogTitle}>Assign Breakpoint Variants</DialogTitle>
                    <IconButton
                        onClick={handleCancel}
                        id="closeIcon"
                        style={{ marginRight: "10px", marginTop: "5px", height: "40px" }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <DialogContent style={{ padding: "0px 24px" }}>
                    <Typography className={classes.subHeading} variant="body2">
                        Assign your Breakpoint Variants to the intervals in which you want them to be shown.
                    </Typography>
                    <Grid container className={classes.breakpointSection}>
                        {breakpointNames.map((name, i) => {
                            const selectedExperience = updatedExpCollections.find(
                                ({ threshold }) => threshold === existingThresholds[i]
                            );
                            const value = selectedExperience ? selectedExperience.experienceId : "";
                            return (
                                <Grid item className={classes.breakpointContainer} key={existingThresholds[i]}>
                                    <div className={classes.intervalIndicator} id={name} />
                                    <span className={classes.breakpointLabel}>{name}</span>
                                    <select
                                        className={classes.selectBreakpoint}
                                        value={value}
                                        onChange={(e) => handleDropdownChange(e, existingThresholds[i])}
                                    >
                                        {uniqueExperiences.map((experienceId, index) => {
                                            // initially currentLanguageExpJSON will be empty object.
                                            const experienceName = currentLanguageExpJSON
                                                ? currentLanguageExpJSON[experienceId].name
                                                : "";
                                            return (
                                                <option key={experienceId} value={experienceId}>{`#${index + 1
                                                    } ${experienceName}`}</option>
                                            );
                                        })}
                                    </select>
                                    <div id="threshold-value" className={classes.breakpointValue}>
                                        {existingThresholds[i]}
                                    </div>
                                </Grid>
                            );
                        })}
                    </Grid>
                    <hr />
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            {breakpointsUpdated ? (
                                <Tooltip
                                    classes={{
                                        popper : classes.tooltipPopper,
                                        tooltip : classes.tooltipLabel,
                                    }} 
                                    arrow
                                    title={tooltipText}
                                    placement="top"
                                    onClick = {handleOnClickUpdate}
                                >
                                    <Button className={classes.btn} startIcon={<ErrorIcon />} variant="outlined">
                                        Update Breakpoints
                                    </Button>
                                </Tooltip>
                            ) : null}
                        </Grid>
                        <Grid item>
                            <Button className={classes.btn} variant="outlined" onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button
                                className={classes.btn}
                                variant="contained"
                                onClick={updateExperienceCollections}
                                type="submit"
                            >
                             {updatingBreakpoints ? <CircularProgress className={classes.circularProgress} size={24} /> : "OK"}
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
            <div className={classes.iconWrapper}>
                <div className={classes.assignBreakpointIcon} onClick={() => setOpen(true)}>
                    {/* This error icon will be used when we have updated the breakpoints from settings */}
                    {breakpointsUpdated ? <ErrorIcon className={classes.exclamationIcon} /> : null}
                </div>
            </div>
        </>
    );
}



// WEBPACK FOOTER //
// ./src/editor/ui/editor/CollectionNavigation/AssignBreakpoint.tsx